import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { ArrowLeft, Shield, Copy, Eye, EyeOff, CheckCircle2, Server, Clock } from 'lucide-react';
import api from '../services/api';

const VPNInfoPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isDark } = useTheme();
  const [vpnData, setVpnData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPasswords, setShowPasswords] = useState({});
  const [copiedField, setCopiedField] = useState(null);

  useEffect(() => {
    loadVPNInfo();
  }, []);

  const loadVPNInfo = async () => {
    try {
      const response = await api.get('/api/vpn/info');
      setVpnData(response.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const togglePassword = (index) => {
    setShowPasswords(prev => ({ ...prev, [index]: !prev[index] }));
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>;

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-white'}`}>
      <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} border-b`}>
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/dashboard')} className={`p-2 rounded-lg ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}>
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Información VPN</h1>
              <p className={`text-sm ${isDark ? 'text-gray-700' : 'text-gray-600'}`}>
                {vpnData?.total_routers || 0} router{vpnData?.total_routers !== 1 ? 'es' : ''}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Instrucciones globales para WinBox */}
        <div className={`${isDark ? 'bg-blue-900/20 border-blue-500/30' : 'bg-blue-50 border-blue-200'} border rounded-xl p-6 mb-8`}>
          <h3 className={`text-lg font-bold mb-3 ${isDark ? 'text-blue-400' : 'text-blue-900'}`}>
            📘 Cómo conectarte a tus routers via WinBox
          </h3>
          <div className={`space-y-2 text-sm ${isDark ? 'text-blue-200' : 'text-blue-800'}`}>
            <p className="font-semibold">Para acceder a la configuración de cualquier router configurado:</p>
            <ol className="list-decimal list-inside space-y-1 ml-2">
              <li>Abre <strong>WinBox</strong></li>
              <li>Usa las credenciales que aparecen en cada router abajo:</li>
              <ul className="list-disc list-inside ml-6 mt-1">
                <li><strong>Usuario:</strong> ETWEB</li>
                <li><strong>Contraseña:</strong> [Ver en el card del router]</li>
                <li><strong>IP:</strong> [Ver "VPN Address" en el card del router]</li>
              </ul>
              <li>Haz clic en "Connect"</li>
              <li>Ya podrás administrar tu MikroTik remotamente</li>
            </ol>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {vpnData?.credentials.map((cred, idx) => (
            <div 
              key={idx} 
              className={`bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow overflow-hidden border-2 ${
                cred.is_configured ? 'border-green-500' : 'border-orange-500'
              } relative`}
            >
              {/* Badge superior */}
              <div className={`${cred.is_configured ? 'bg-green-500' : 'bg-orange-500'} text-white px-4 py-2 flex items-center justify-between`}>
                <span className="font-semibold text-sm">
                  {cred.router_name}
                </span>
                <span className="text-xs font-bold">
                  {cred.is_configured ? '✓ CONFIGURADO' : '⏳ PENDIENTE'}
                </span>
              </div>
              
              <div className="p-6">

              <div className="space-y-3">
                <div>
                  <label className={`text-xs font-semibold text-gray-700 ${isDark ? 'text-gray-700' : 'text-gray-600'}`}>Dirección VPN</label>
                  <div className="flex gap-2 mt-1">
                    <input type="text" value={cred.vpn_address} readOnly className={`flex-1 px-3 py-2 rounded-lg text-sm font-mono ${isDark ? 'bg-white text-gray-900 border-gray-300' : 'bg-white'} border ${isDark ? 'border-gray-600' : 'border-gray-300'}`} />
                    <button onClick={() => copyToClipboard(cred.vpn_address, `addr-${idx}`)} className={`px-3 py-2 rounded-lg ${isDark ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-200 hover:bg-gray-300'}`}>
                      {copiedField === `addr-${idx}` ? <CheckCircle2 className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className={`text-xs font-semibold text-gray-700 ${isDark ? 'text-gray-700' : 'text-gray-600'}`}>Usuario</label>
                  <div className="flex gap-2 mt-1">
                    <input type="text" value={cred.username} readOnly className={`flex-1 px-3 py-2 rounded-lg text-sm font-mono ${isDark ? 'bg-white text-gray-900 border-gray-300' : 'bg-white'} border ${isDark ? 'border-gray-600' : 'border-gray-300'}`} />
                    <button onClick={() => copyToClipboard(cred.username, `user-${idx}`)} className={`px-3 py-2 rounded-lg ${isDark ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-200 hover:bg-gray-300'}`}>
                      {copiedField === `user-${idx}` ? <CheckCircle2 className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className={`text-xs font-semibold text-gray-700 ${isDark ? 'text-gray-700' : 'text-gray-600'}`}>Contraseña</label>
                  <div className="flex gap-2 mt-1">
                    <input type={showPasswords[idx] ? 'text' : 'password'} value={cred.password} readOnly className={`flex-1 px-3 py-2 rounded-lg text-sm font-mono ${isDark ? 'bg-white text-gray-900 border-gray-300' : 'bg-white'} border ${isDark ? 'border-gray-600' : 'border-gray-300'}`} />
                    <button onClick={() => togglePassword(idx)} className={`px-3 py-2 rounded-lg ${isDark ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-200 hover:bg-gray-300'}`}>
                      {showPasswords[idx] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                    <button onClick={() => copyToClipboard(cred.password, `pass-${idx}`)} className={`px-3 py-2 rounded-lg ${isDark ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-200 hover:bg-gray-300'}`}>
                      {copiedField === `pass-${idx}` ? <CheckCircle2 className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div className={`text-xs pt-3 border-t ${isDark ? 'border-gray-700 text-gray-700' : 'border-gray-200 text-gray-600'}`}>
                  <p>VPN User: {cred.vpn_username}</p>
                  <p>IP Interna: {cred.internal_ip}</p>
                </div>
              </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default VPNInfoPage;
